import { addHours, differenceInMinutes, parseISO } from "date-fns";

import { type ShiftTimeData } from "./types";

export const OVERWORKING_THRESHOLD_IN_HOURS = 17;
export const OVERWORKING_WINDOW_SIZE_IN_HOURS = 24;

/**
 * Checks if adding a proposed shift to existing shifts would cause overworking
 * (>17 hours within any 24-hour window).
 *
 * The rule: Within any 24-hour period, a worker cannot work more than 17 hours.
 *
 * 1. Combine proposed shift with existing shifts and sort by start time
 * 2. Only check windows that include the proposed shift
 * 3. For each such window, sum the scheduled duration of all shifts in that window
 * 4. If any window exceeds 17 hours, return true
 */
export function wouldProposedShiftCauseOverworking(
  proposedShift: ShiftTimeData,
  existingShifts: ShiftTimeData[]
): boolean {
  if (existingShifts.length === 0) {
    return false;
  }

  const allShifts = [proposedShift, ...existingShifts]
    .map((shift) => ({
      start: parseISO(shift.start),
      end: parseISO(shift.end),
      isProposed: shift === proposedShift,
    }))
    .sort((a, b) => a.start.getTime() - b.start.getTime());

  const proposedShiftStart = parseISO(proposedShift.start);

  for (let index = 0; index < allShifts.length; index++) {
    const windowStart = allShifts[index].start;
    const windowEnd = addHours(windowStart, OVERWORKING_WINDOW_SIZE_IN_HOURS);

    const proposedInWindow = proposedShiftStart >= windowStart && proposedShiftStart < windowEnd;
    if (!proposedInWindow) {
      continue;
    }

    let totalMinutes = 0;
    for (let shiftIndex = index; shiftIndex < allShifts.length; shiftIndex++) {
      const shift = allShifts[shiftIndex];
      if (shift.start >= windowEnd) {
        break;
      }

      const startsInWindow = shift.start >= windowStart && shift.start < windowEnd;
      const endsInWindow = shift.end > windowStart && shift.end <= windowEnd;

      if (startsInWindow || endsInWindow) {
        const shiftDurationMinutes = differenceInMinutes(shift.end, shift.start);
        totalMinutes += shiftDurationMinutes;
      }
    }

    const totalHours = totalMinutes / 60;

    if (totalHours > OVERWORKING_THRESHOLD_IN_HOURS) {
      return true;
    }
  }

  return false;
}
